import { useState } from 'react'
import { useProject } from '../components/ProjectLayout.js'
import { ErrorBox, PageHeader } from '../components/ui.js'
import { useApi } from '../lib/useApi.js'
import type { Diagnosis } from '../lib/api.js'

export default function Diagnostics() {
  const { project } = useProject()
  const api = useApi()
  const [result, setResult] = useState<Diagnosis | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  return (
    <>
      <PageHeader
        title="Diagnostics"
        description="Inspect project configuration and get actionable findings."
      />
      <section className="card panel">
        <h2>Check your project</h2>
        <p className="sub">
          Read-only checks. These do not modify your tables, policies or deployment. Results describe the
          configuration checked, not a guarantee of end-to-end application health.
        </p>
        <button
          className="btn btn-primary"
          disabled={busy}
          onClick={async () => {
            setBusy(true)
            setError(null)
            setResult(null)
            try {
              setResult(await api.diagnoseProject(project.id))
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Could not run checks.')
            } finally {
              setBusy(false)
            }
          }}
        >
          {busy ? 'Checking…' : 'Run diagnostics'}
        </button>
      </section>
      {error && <ErrorBox>{error}</ErrorBox>}
      {result && (
        <section aria-live="polite">
          <h2>{result.summary}</h2>
          {result.findings.map((f, i) => (
            <div className="card panel" key={`${f.code}-${i}`}>
              <span className={`pill ${f.severity === 'blocking' ? 'pill-dead' : 'pill-warn'}`}>
                {f.severity}
              </span>
              <h3>{f.code.replaceAll('_', ' ')}</h3>
              <p>{f.detail}</p>
              {f.symptom && <p className="sub">{f.symptom}</p>}
              {f.fix && (
                <p>
                  <strong>Next step:</strong> {f.fix}
                </p>
              )}
            </div>
          ))}
          <details className="guide">
            <summary>Checks performed ({result.checked.length})</summary>
            <ul>
              {result.checked.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </details>
        </section>
      )}
    </>
  )
}
